import { motion } from 'framer-motion'
import { CheckCircle } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import Confetti from 'react-confetti'

import { getCheckoutSession } from '../../services'
import logger from '../../utils/logger'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams()
  const [sessionInfo, setSessionInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const sessionId = searchParams.get('session_id')
  const navigate = useNavigate()
  const [showConfetti, setShowConfetti] = useState(true)

  useEffect(() => {
    const fetchSession = async () => {
      if (!sessionId) return
      try {
        const data = await getCheckoutSession({ sessionId })
        setSessionInfo(data)
      } catch (error) {
        logger.error(error)
        toast.error(error?.response?.data?.error || 'Failed to load session')
      } finally {
        setLoading(false)
      }
    }
    fetchSession()

    // Stop confetti after 5 seconds
    const timer = setTimeout(() => setShowConfetti(false), 10000)
    return () => clearTimeout(timer)
  }, [sessionId])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-green-50 to-green-100">
        <p className="text-green-600 text-lg animate-pulse">Loading payment details…</p>
      </div>
    )
  }

  if (!sessionInfo) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-red-100">
        <p className="text-red-500 text-lg">Could not retrieve payment information.</p>
      </div>
    )
  }

  const { amount_total, currency, customer_details, subscription, invoice, status } = sessionInfo

  const subscriptionObj = subscription || {}
  const nextBillingTs = subscriptionObj.current_period_end
  const nextBillingDate = nextBillingTs
    ? new Date(nextBillingTs * 1000).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null

  return (
    <div className="relative flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100 p-6">
      {showConfetti && <Confetti width={window.innerWidth} height={window.innerHeight} />}

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-xl"
      >
        <Card className="rounded-3xl shadow-2xl border-0 overflow-hidden">
          <div className="bg-green-600 py-8 px-6 flex flex-col items-center text-center">
            <motion.div
              initial={{ scale: 0.7 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
            >
              <CheckCircle className="w-20 h-20 text-white drop-shadow-lg" />
            </motion.div>
            <h2 className="text-3xl font-bold text-white mt-4">Payment Successful</h2>
            <p className="text-green-100 mt-1 text-sm">Thank you for your payment! 🎉</p>
          </div>

          <CardContent className="p-6 space-y-5 text-gray-700">
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b bg-gray-50">
                    <td className="px-4 py-3 font-medium w-1/2">Customer Email</td>
                    <td className="px-4 py-3 text-right break-all">
                      {customer_details?.email || '—'}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 font-medium">Amount Paid</td>
                    <td className="px-4 py-3 text-right text-green-700 font-semibold">
                      {amount_total != null
                        ? (amount_total / 100).toFixed(2) + ' ' + currency.toUpperCase()
                        : '—'}
                    </td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="px-4 py-3 font-medium">Subscription ID</td>
                    <td className="px-4 py-3 text-right font-mono text-xs break-all">
                      {subscriptionObj.id || '—'}
                    </td>
                  </tr>
                  {invoice && (
                    <tr className="border-b">
                      <td className="px-4 py-3 font-medium">Invoice ID</td>
                      <td className="px-4 py-3 text-right font-mono text-xs break-all">
                        {invoice}
                      </td>
                    </tr>
                  )}
                  {nextBillingDate && (
                    <tr className="border-b bg-gray-50">
                      <td className="px-4 py-3 font-medium">Next Billing Date</td>
                      <td className="px-4 py-3 text-right">{nextBillingDate}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="px-4 py-3 font-medium">Session Status</td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-block px-3 py-1 text-xs rounded-full bg-green-100 text-green-800 capitalize">
                        {status}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                className="w-full sm:w-1/2 text-white font-semibold bg-green-600 hover:bg-green-700 transition"
                onClick={() => navigate('/business/dashboard')}
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outline"
                className="w-full sm:w-1/2"
                onClick={() => navigate('/business/profile/invoice')}
              >
                View Invoices
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}

export default PaymentSuccessPage
